/**
 * Edge auto-pan tuning (#217 v2). The board scrolls horizontally; when the drag
 * cursor enters EDGE_ZONE_PX of an edge, autoScrollDelta returns a signed
 * px/frame nudge, ramped by proximity and capped at MAX_AUTOSCROLL_PX.
 *
 * Tuning invariant: the distance auto-scroll can travel within one #218
 * observer cooldown (~0.5s) must stay well under WEEK_PX (= 7×(180+12) = 1344px),
 * else a slide can be skipped mid-pan and scrollLeft briefly stalls at the
 * mounted edge. At 18px/frame × ~60fps × 0.5s ≈ 540px ≪ 1344px — comfortable.
 * Don't raise MAX_AUTOSCROLL_PX past ~40 without re-checking this.
 */
export const EDGE_ZONE_PX = 80;
export const MAX_AUTOSCROLL_PX = 18;

const clamp01 = (x: number) => Math.min(1, Math.max(0, x));

/**
 * Signed horizontal auto-scroll speed (px/frame) for a drag cursor at `cursorX`
 * over a container whose viewport spans [rect.left, rect.right].
 *   negative → scroll left · positive → scroll right · 0 → idle (middle).
 * Ramps linearly from 0 at the inner zone boundary to ±maxSpeed at the edge,
 * clamped past the physical edge so leaving the window doesn't over-speed.
 */
export function autoScrollDelta(
  cursorX: number,
  rect: { left: number; right: number },
  zonePx: number,
  maxSpeed: number,
): number {
  if (zonePx <= 0) return 0;
  if (cursorX < rect.left + zonePx) {
    return -maxSpeed * clamp01(1 - (cursorX - rect.left) / zonePx);
  }
  if (cursorX > rect.right - zonePx) {
    return maxSpeed * clamp01(1 - (rect.right - cursorX) / zonePx);
  }
  return 0;
}
